import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Mic, Monitor, Upload, Loader2, AlertTriangle, X, Check, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import ClipVideo from "@/components/ClipVideo";
import { useAuth } from "@/context/AuthContext";
import AvisoWindows from "@/components/AvisoWindows";

// Línea para quien ya tiene su propia forma de grabar. Va DEBAJO de las instrucciones del atajo:
// el camino principal sigue siendo el que no requiere instalar nada.
const YA_GRABA = "Y si ya usás algún programa para grabar la pantalla —Loom, OBS, el que sea— usalo tranquilo y subime el video igual. No importa con qué lo grabes.";

// Instrucciones por sistema operativo. Se muestra SOLO la del SO de la persona (nunca las dos juntas).
const INSTR = {
  mac: {
    label: "Mac",
    intro: "Tu Mac ya lo trae, no instalás nada.",
    pasos: [
      "Apretá a la vez Shift, Comando y el 5.",
      "Se abre una barrita abajo. Antes de grabar tocá Opciones y elegí tu micrófono, así se escucha lo que contás.",
      "Tocá Grabar, y hacé tu proceso una vez, despacio, mientras me lo contás en voz alta y vas marcando con el cursor dónde tocás.",
      "Para terminar, apretá Comando, Control y Escape.",
      "El video te queda en el escritorio. Arrastralo acá.",
    ],
    aviso: null,
  },
  windows: {
    label: "Windows",
    intro: "Tu Windows ya lo trae, no instalás nada.",
    pasos: [
      "Abrí primero el programa con el que trabajás y dejalo adelante.",
      "Apretá a la vez la tecla Windows, Alt y la R. Empieza a grabar.",
      "Hacé tu proceso una vez, despacio, mientras me lo contás en voz alta y vas marcando con el cursor dónde tocás.",
      "Apretá otra vez Windows, Alt y R para terminar.",
      "El video te queda en la carpeta Vídeos, adentro de Capturas. Arrastralo acá.",
    ],
    aviso:
      "Una cosa importante de Windows: graba solo la ventana del programa que tenías adelante cuando arrancaste. No graba el escritorio ni las carpetas. Si tu proceso usa dos programas, hacé una grabación de cada uno.",
  },
};

const normSo = (s) => {
  const t = (s || "").toLowerCase();
  if (t.includes("mac") || t.includes("apple") || t.includes("os x")) return "mac";
  if (t.includes("win")) return "windows";
  return "";
};

const ORIGEN_LABEL = { voz: "lo dijiste", pantalla: "se ve en pantalla", ambas: "lo dijiste y se ve" };

export default function MostrarPantalla({ so = "", procesoId, onHablando, onListo, onCerrar }) {
  const { user, setUser } = useAuth();
  const [soLocal, setSoLocal] = useState(normSo(so) || normSo(user?.sistema_operativo));
  const [fase, setFase] = useState("oferta"); // oferta | os | instrucciones | procesando | borrador | fallo
  const [draft, setDraft] = useState(null);
  const [clipId, setClipId] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [avisoWin, setAvisoWin] = useState(false);
  const fileRef = useRef(null);

  // Windows: si todavía no eligió, abrimos el pop-up antes de seguir (una vez por cuenta).
  const elegirWindows = () => {
    if (user?.decision_so) { setSo("windows"); return; }
    setAvisoWin(true);
  };
  const resolverAvisoWin = (dec) => {
    setAvisoWin(false);
    setSo(dec === "uso_mac" ? "mac" : "windows");
  };

  // Video tutorial (opcional, cargado por el equipo). El paso se ve igual sin él.
  useEffect(() => {
    if (fase !== "instrucciones") return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/clips`, { params: { pantalla: "mostrar_pantalla" } });
        if (alive) setClipId((data?.clips || {}).grabar || null);
      } catch (e) { /* noop */ }
    })();
    return () => { alive = false; };
  }, [fase]);

  const elegirGrabar = () => {
    if (soLocal) setFase("instrucciones");
    else setFase("os");
  };

  const setSo = async (v) => {
    setSoLocal(v);
    setFase("instrucciones");
    try {
      await api.post("/preparar", { datos: { sistema_operativo: v, plataforma_trabajo: "computadora" } });
      setUser && setUser((u) => (u ? { ...u, sistema_operativo: v } : u));
    } catch (e) { /* no bloqueamos el flujo si no se pudo guardar */ }
  };

  const onArchivo = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["mp4", "mov", "webm", "mkv"].includes(ext)) {
      toast.error("Eso no es un video. Aceptamos .mp4, .mov, .webm o .mkv.");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error("El video pesa más de 100 MB. Grabá un tramo más corto o seguimos con capturas.");
      return;
    }
    setFase("procesando");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/proceso/${procesoId}/mostrar-pantalla`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setDraft(data);
      setFase("borrador");
    } catch (e) {
      setFase("fallo");
    }
  };

  const confirmar = async () => {
    if (!draft || confirmando) return;
    setConfirmando(true);
    try {
      const { data } = await api.post(`/proceso/${procesoId}/mostrar-pantalla/confirmar`, {
        pasos: draft.pasos, herramienta: draft.herramienta || "",
      });
      toast.success("Listo, sumé el paso a paso de tu video.");
      onListo && onListo(data);
    } catch (e) {
      toast.error(errText(e) || "No pude guardar el paso a paso. Probá de nuevo.");
    } finally {
      setConfirmando(false);
    }
  };

  const cerrar = () => { onCerrar && onCerrar(); };
  const instr = soLocal ? INSTR[soLocal] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-xl p-4 mb-3"
      style={{ border: "1px solid rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.06)" }}
      data-testid="mostrar-pantalla"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Eye size={16} className="text-dorado shrink-0" />
          <span className="mono-label text-dorado">MOSTRAME TU PANTALLA</span>
        </div>
        <button onClick={cerrar} className="text-gris hover:text-crema transition-colors shrink-0" data-testid="mostrar-pantalla-cerrar" aria-label="Cerrar">
          <X size={16} />
        </button>
      </div>

      {fase === "oferta" && (
        <div data-testid="mostrar-pantalla-oferta">
          <p className="text-crema text-sm leading-relaxed mb-2">
            Che, una cosa: en vez de escribirme el paso a paso, <span className="text-dorado">contámelo</span> — es mucho más fácil y no te salteás nada.
          </p>
          <p className="text-crema text-sm leading-relaxed mb-4">
            Podés mandarme un <span className="text-dorado">audio</span> contando todos los pasos como si me los explicaras, o grabar un <span className="text-dorado">video de tu pantalla</span> haciendo el proceso una vez —bien despacio y mostrando bien el cursor en cada paso, con un máximo de cinco minutos—. Yo lo tomo de ahí.
          </p>
          <div className="flex flex-wrap gap-2.5">
            <button
              onClick={() => onHablando && onHablando()}
              className="btn-fantasma text-sm flex items-center gap-2"
              data-testid="mostrar-pantalla-hablando"
            >
              <Mic size={15} /> CONTÁMELO POR AUDIO
            </button>
            <button
              onClick={elegirGrabar}
              className="btn-dorado text-sm !py-2 flex items-center gap-2"
              data-testid="mostrar-pantalla-grabar"
            >
              <Monitor size={15} /> GRABÁ TU PANTALLA
            </button>
          </div>
        </div>
      )}

      {fase === "os" && (
        <div data-testid="mostrar-pantalla-os">
          <p className="text-crema text-sm mb-3">¿En qué computadora trabajás?</p>
          <div className="flex gap-2.5">
            <button onClick={() => setSo("mac")} className="btn-fantasma text-sm flex-1" data-testid="mostrar-pantalla-os-mac">Mac</button>
            <button onClick={elegirWindows} className="btn-fantasma text-sm flex-1" data-testid="mostrar-pantalla-os-windows">Windows</button>
          </div>
          <AvisoWindows open={avisoWin} onResolver={resolverAvisoWin} />
        </div>
      )}

      {fase === "instrucciones" && instr && (
        <div data-testid="mostrar-pantalla-instrucciones">
          {clipId && <ClipVideo clipId={clipId} pantalla="mostrar_pantalla" paso_key="grabar" />}
          <p className="text-crema text-sm mb-2">{instr.intro}</p>
          <ol className="text-gris text-sm leading-relaxed space-y-1.5 mb-3 list-decimal pl-5" data-testid={`mostrar-pantalla-pasos-${soLocal}`}>
            {instr.pasos.map((p, i) => (<li key={i}>{p}</li>))}
          </ol>
          {instr.aviso && (
            <div className="rounded-lg p-3 mb-3 text-xs text-crema" style={{ border: "1px solid rgba(201,168,76,0.3)", background: "rgba(201,168,76,0.05)" }} data-testid="mostrar-pantalla-aviso-windows">
              {instr.aviso}
            </div>
          )}
          <p className="text-gris text-sm leading-relaxed mb-3" data-testid="mostrar-pantalla-ya-graba">
            {YA_GRABA}
          </p>
          <div className="rounded-lg p-3 mb-3 text-xs text-gris space-y-2" style={{ border: "1px solid #2A2A2A", background: "rgba(255,255,255,0.02)" }} data-testid="mostrar-pantalla-advertencias">
            <div className="flex items-start gap-2"><AlertTriangle size={13} className="text-gris shrink-0 mt-0.5" /><span>No escribas contraseñas ni dejes datos privados a la vista mientras grabás. Queda todo en el video.</span></div>
            <div className="flex items-start gap-2"><AlertTriangle size={13} className="text-gris shrink-0 mt-0.5" /><span>Que dure poco: con tres o cuatro minutos alcanza y el máximo son cinco minutos. Hacé el proceso una vez, tranquilo. (El video puede pesar hasta 100 MB.)</span></div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".mp4,.mov,.webm,.mkv,video/mp4,video/quicktime,video/webm,video/x-matroska"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onArchivo(f); e.target.value = ""; }}
            data-testid="mostrar-pantalla-file"
          />
          <div
            onClick={() => fileRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f) onArchivo(f); }}
            onDragOver={(e) => e.preventDefault()}
            className="rounded-xl border border-dashed border-dorado/50 py-6 px-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-dorado transition-colors"
            data-testid="mostrar-pantalla-dropzone"
          >
            <Upload size={20} className="text-dorado" />
            <span className="text-crema text-sm text-center">Arrastrá tu video acá, o tocá para elegirlo</span>
            <span className="mono-label text-gris" style={{ fontSize: "10px" }}>.MP4 · .MOV · .WEBM · .MKV · HASTA 100 MB</span>
          </div>
        </div>
      )}

      {fase === "procesando" && (
        <div className="flex items-center gap-3 py-4" data-testid="mostrar-pantalla-procesando">
          <Loader2 size={18} className="animate-spin text-dorado" />
          <span className="text-crema text-sm">Estoy mirando tu video y leyendo lo que se ve en pantalla. Puede tardar un rato.</span>
        </div>
      )}

      {fase === "borrador" && draft && (
        <div data-testid="mostrar-pantalla-borrador">
          <p className="text-crema text-sm mb-3">Miré tu video. Esto es lo que entendí:</p>
          <ol className="space-y-2 mb-4 list-decimal pl-5" data-testid="mostrar-pantalla-borrador-pasos">
            {(draft.pasos || []).map((p, i) => (
              <li key={i} className="text-crema text-sm">
                {p.texto}
                {p.origen && ORIGEN_LABEL[p.origen] && (
                  <span className="mono-label text-gris ml-2" style={{ fontSize: "9px" }}>({ORIGEN_LABEL[p.origen]})</span>
                )}
              </li>
            ))}
          </ol>
          {!draft.tiene_audio && (
            <p className="text-gris text-xs mb-3" data-testid="mostrar-pantalla-sin-audio">
              El video no tenía tu voz, así que lo saqué solo de lo que se ve en pantalla. Quizás tenga que preguntarte algún nombre que no quedó claro.
            </p>
          )}
          {(draft.no_leidos || []).length > 0 && (
            <div className="rounded-lg p-3 mb-4 text-sm" style={{ border: "1px solid rgba(201,168,76,0.3)", background: "rgba(201,168,76,0.05)" }} data-testid="mostrar-pantalla-no-leidos">
              <div className="mono-label text-dorado mb-2" style={{ fontSize: "10px" }}>ESTO NO LO PUDE LEER — DESPUÉS ME LO COMPLETÁS</div>
              <ul className="space-y-1.5">
                {draft.no_leidos.map((n, i) => (
                  <li key={i} className="text-crema flex gap-2"><span className="text-dorado shrink-0">·</span><span>{n}</span></li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2.5">
            <button onClick={confirmar} disabled={confirmando} className="btn-dorado text-sm !py-2 flex items-center gap-2" data-testid="mostrar-pantalla-confirmar">
              <Check size={15} /> {confirmando ? "GUARDANDO…" : "ESTÁ BIEN, USALO"}
            </button>
            <button onClick={() => setFase("instrucciones")} disabled={confirmando} className="mono-label text-gris hover:text-crema" style={{ fontSize: "11px" }} data-testid="mostrar-pantalla-rehacer">
              grabar de nuevo
            </button>
          </div>
        </div>
      )}

      {fase === "fallo" && (
        <div data-testid="mostrar-pantalla-fallo">
          <p className="text-crema text-sm mb-3">
            No pude con ese video (puede ser muy largo o pesado). No pasa nada: seguimos con capturas de pantalla, que es igual de bueno.
          </p>
          <div className="flex flex-wrap items-center gap-2.5">
            <button onClick={() => setFase("instrucciones")} className="btn-fantasma text-sm" data-testid="mostrar-pantalla-reintentar">PROBAR OTRO VIDEO</button>
            <button onClick={cerrar} className="btn-dorado text-sm !py-2" data-testid="mostrar-pantalla-capturas">PREFIERO MANDAR CAPTURAS</button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
